import apiClient from '@/api/client'
import { ApiError, handleApiError } from '@/api/errorHandler'
import { decodeRelayId } from '@/utils/id'
import {
  PAYOUT_TEXT_MAX_LENGTH,
  validatePayoutDocument,
  type PayoutDocumentErrorCode,
} from '@/lib/payoutDocument'
import type { PayoutStatement, PayoutStatementBackend } from '@/types/financials'
import axios from 'axios'

/** Statement payload common to the approve and dispute responses. */
interface PayoutStatementActionPayload {
  id: string
  statement_number: string
  status: string
  gross_amount: string
  net_amount: string
  /**
   * Signed, expiring URL for the uploaded document. Regenerated on every read —
   * never cache or persist it; refetch the statement for a fresh one.
   */
  supporting_document_url?: string | null
  supporting_document_name?: string | null
}

/** Response from POST /api/v1/finance/payout/statements/approve/ */
export interface ApprovePayoutResponse {
  success: boolean
  statement?: PayoutStatementActionPayload & {
    /** Internal, ops-only notes. Never render where a provider can see it. */
    notes?: string
  }
  message?: string
}

/** Response from POST /api/v1/finance/payout/statements/dispute/ */
export interface DisputePayoutResponse {
  success: boolean
  statement?: PayoutStatementActionPayload & {
    provider_notes?: string
  }
  message?: string
}

/**
 * Backend response format variations.
 */
type ListResponseShape =
  | PayoutStatementBackend[]
  | { statements: PayoutStatementBackend[]; total_count?: number; limit?: number; offset?: number; has_next?: boolean }
  | { results: PayoutStatementBackend[]; count?: number }
  | { items: PayoutStatementBackend[]; total?: number }
  | { data: PayoutStatementBackend[] }

/** A single page of payout statements. */
export interface PayoutStatementsPage {
  statements: PayoutStatement[]
  /** Total number of statements matching the filters (across all pages). */
  totalCount: number
  limit: number
  offset: number
  hasNext: boolean
}

/**
 * Transform backend payout statement to frontend format.
 * Handles field name mapping and type conversions.
 */
function transformBackendStatement(backend: PayoutStatementBackend): PayoutStatement {
  const grossAmount = parseFloat(backend.gross_amount) || 0
  const netAmount = parseFloat(backend.net_amount) || 0
  
  // Calculate legacy fields from new backend format
  // totalProviderCut = netAmount (what provider receives after deductions)
  // totalProviderVat = grossAmount - netAmount (platform commission/deductions)
  const totalProviderCut = netAmount
  const totalProviderVat = grossAmount - netAmount
  
  return {
    id: backend.id,
    statementNumber: backend.statement_number,
    providerId: backend.provider_id,
    providerName: backend.provider_name,
    // For backwards compatibility with UI that uses organizationId/Name
    organizationId: backend.provider_id,
    organizationName: backend.provider_name,
    periodStart: backend.period_start,
    periodEnd: backend.period_end,
    status: backend.status,
    // New backend fields
    grossAmount,
    netAmount,
    // Legacy fields (computed)
    totalProviderCut,
    totalProviderVat,
    totalAmount: grossAmount,
    // Payout details will be empty from list endpoint (need separate call for details)
    payoutDetails: [],
    payoutCount: backend.payout_count,
    createdAt: backend.created_at,
    // Permissions based on status
    canEdit: backend.status === 'draft',
    canConfirmByProvider: backend.status === 'draft' || backend.status === 'pending_review',
    canConfirmByWtd: backend.status === 'pending_review',
  }
}

/**
 * Extract and normalize list from various response shapes.
 */
function normalizeListResponse(data: unknown): PayoutStatement[] {
  let rawList: PayoutStatementBackend[] = []
  
  if (Array.isArray(data)) {
    rawList = data as PayoutStatementBackend[]
  } else if (data && typeof data === 'object') {
    const obj = data as any
    if (Array.isArray(obj.statements)) rawList = obj.statements
    else if (Array.isArray(obj.results)) rawList = obj.results
    else if (Array.isArray(obj.items)) rawList = obj.items
    else if (Array.isArray(obj.data)) rawList = obj.data
  }
  
  // Check if data is already in frontend format (has totalProviderCut) or needs transformation
  if (rawList.length > 0 && 'statement_number' in rawList[0]) {
    // New backend format - transform
    return rawList.map(transformBackendStatement)
  }
  
  // Already in frontend format or empty
  return rawList as unknown as PayoutStatement[]
}

/**
 * Extract pagination metadata from the list response.
 *
 * The backend returns `{ statements, total_count, limit, offset, has_next }`.
 * Older/alternative shapes (bare array, `results`/`count`, `items`/`total`)
 * are handled so the caller always gets usable page metadata.
 */
function normalizePageMeta(
  data: unknown,
  statements: PayoutStatement[],
  requested: { limit: number; offset: number }
): Omit<PayoutStatementsPage, 'statements'> {
  const obj = (data && typeof data === 'object' && !Array.isArray(data) ? data : {}) as any

  const totalCount =
    typeof obj.total_count === 'number'
      ? obj.total_count
      : typeof obj.count === 'number'
        ? obj.count
        : typeof obj.total === 'number'
          ? obj.total
          : requested.offset + statements.length

  const limit = typeof obj.limit === 'number' ? obj.limit : requested.limit
  const offset = typeof obj.offset === 'number' ? obj.offset : requested.offset
  const hasNext =
    typeof obj.has_next === 'boolean' ? obj.has_next : offset + statements.length < totalCount

  return { totalCount, limit, offset, hasNext }
}

/** Page size used when the caller does not specify one. */
const DEFAULT_PAGE_SIZE = 50

/** Largest page size requested when walking every page. */
const MAX_PAGE_SIZE = 100

/** Safety cap on the number of pages fetched by listAllPayoutStatements. */
const MAX_PAGES = 50

/**
 * Finance API (REST) for payout statements.
 *
 * New backend endpoints:
 * - GET  /api/v1/finance/payout/statements/
 * - POST /api/v1/finance/payout/statements/approve/
 *
 * Query Parameters:
 * - provider_id: Filter by provider UUID
 * - status: Filter by status (draft, pending_review, approved, paid, etc.)
 */
export const financialsApi = {
  /**
   * Fetch a single page of payout statements.
   *
   * Backend expects:
   * - provider_id = ProfileProvider.id (UUID)
   *
   * IMPORTANT: Do NOT pass organization/company IDs here (even if UUID),
   * and never pass Relay global IDs. Use `providerId` only when you have
   * the provider profile UUID.
   *
   * @param params.providerId - ProfileProvider.id (UUID)
   * @param params.status - Filter by statement status (single status only —
   *   the backend does not accept a comma-separated list)
   * @param params.limit - Page size (backend default is 50)
   * @param params.offset - Number of statements to skip
   */
  async listPayoutStatementsPage(params: {
    providerId?: string | null
    status?: string | null
    limit?: number
    offset?: number
  } = {}): Promise<PayoutStatementsPage> {
    // Decode relay ID if provided (for backwards compatibility)
    const providerId = params.providerId ? (decodeRelayId(params.providerId) ?? params.providerId) : undefined

    const limit = params.limit ?? DEFAULT_PAGE_SIZE
    const offset = params.offset ?? 0

    const queryParams: Record<string, string> = {
      limit: String(limit),
      offset: String(offset),
    }

    if (providerId) {
      queryParams.provider_id = providerId
    }
    
    if (params.status) {
      queryParams.status = params.status
    }

    const response = await apiClient.get<ListResponseShape>('/api/v1/finance/payout/statements/', {
      params: queryParams,
    })

    const statements = normalizeListResponse(response.data)

    return {
      statements,
      ...normalizePageMeta(response.data, statements, { limit, offset }),
    }
  },

  /**
   * Fetch payout statements (first page only).
   *
   * Prefer `listPayoutStatementsPage` when you need pagination metadata, or
   * `listAllPayoutStatements` when you need the complete set.
   */
  async listPayoutStatements(params: { 
    providerId?: string | null
    status?: string | null
    limit?: number
    offset?: number
  } = {}): Promise<PayoutStatement[]> {
    const page = await financialsApi.listPayoutStatementsPage(params)
    return page.statements
  },

  /**
   * Fetch every payout statement by walking the paginated endpoint.
   *
   * Needed wherever totals/counts must cover the whole data set (KPI cards,
   * tab counts, dashboard revenue) rather than just the first page.
   * Stops at MAX_PAGES as a safety net against a runaway `has_next`.
   */
  async listAllPayoutStatements(params: {
    providerId?: string | null
    status?: string | null
    pageSize?: number
  } = {}): Promise<PayoutStatement[]> {
    const pageSize = params.pageSize ?? MAX_PAGE_SIZE
    const all: PayoutStatement[] = []
    let offset = 0

    for (let i = 0; i < MAX_PAGES; i++) {
      const page = await financialsApi.listPayoutStatementsPage({
        providerId: params.providerId,
        status: params.status,
        limit: pageSize,
        offset,
      })

      all.push(...page.statements)

      if (!page.hasNext || page.statements.length === 0) break
      offset += page.statements.length
    }

    return all
  },

  /**
   * Approve a payout statement.
   *
   * Backend: POST /api/v1/finance/payout/statements/approve/ (multipart/form-data)
   * Fields: statement_id, document (required), notes (optional, max 2000 chars)
   *
   * A JSON body is rejected with 400 `document_required` — the supporting
   * document is mandatory.
   *
   * FSM transition: draft → pending_review → approved
   */
  async approvePayoutStatement(input: {
    statementId: string
    document: File
    notes?: string | null
  }): Promise<ApprovePayoutResponse> {
    return postPayoutStatementAction<ApprovePayoutResponse>(
      '/api/v1/finance/payout/statements/approve/',
      input.statementId,
      input.document,
      'notes',
      input.notes
    )
  },

  /**
   * Dispute a payout statement.
   *
   * Backend: POST /api/v1/finance/payout/statements/dispute/ (multipart/form-data)
   * Fields: statement_id, document (required), reason (max 2000 chars)
   *
   * Identical to approve except the text field is `reason`.
   * Only statements in approved, processing, or paid can be disputed.
   */
  async disputePayoutStatement(input: {
    statementId: string
    document: File
    reason?: string | null
  }): Promise<DisputePayoutResponse> {
    return postPayoutStatementAction<DisputePayoutResponse>(
      '/api/v1/finance/payout/statements/dispute/',
      input.statementId,
      input.document,
      'reason',
      input.reason
    )
  },

  /**
   * Get detailed payout information for a specific statement.
   * 
   * Backend: GET /api/v1/finance/payout/statements/{id}/
   * Returns the statement with nested `payouts` array containing
   * individual payout items with order_custom_id, provider_name, amounts, etc.
   *
   * `supportingDocumentUrl` is a signed, expiring link regenerated on every
   * read. Render it straight from this result — never cache or persist it.
   */
  async getPayoutStatementDetails(statementId: string): Promise<PayoutStatement | null> {
    const id = decodeRelayId(statementId) ?? statementId
    
    try {
      const response = await apiClient.get(`/api/v1/finance/payout/statements/${id}/`)
      
      if (!response.data) return null

      const data = response.data as any
      const grossAmount = parseFloat(data.gross_amount) || 0
      const netAmount = parseFloat(data.net_amount) || 0
      const taxDeduction = parseFloat(data.tax_deduction) || 0
      const otherDeductions = parseFloat(data.other_deductions) || 0

      const rawPayouts: any[] = Array.isArray(data.payouts) ? data.payouts : []

      const payoutDetails = rawPayouts.map((p: any) => ({
        id: p.id || undefined,
        serviceId: p.service_id || null,
        serviceName: p.service_name || null,
        serviceDate: p.service_date || null,
        orderCustomId: p.order_custom_id || null,
        providerName: p.provider_name || data.provider_name || null,
        providerCut: parseFloat(p.net_amount || 0),
        providerVat: parseFloat(p.commission_amount || 0),
        grossAmount: parseFloat(p.gross_amount || 0) || undefined,
        netAmount: parseFloat(p.net_amount || 0) || undefined,
        commissionRate: parseFloat(p.commission_rate || 0) || undefined,
        commissionAmount: parseFloat(p.commission_amount || 0) || undefined,
      }))

      return {
        id: data.id,
        statementNumber: data.statement_number,
        providerId: data.provider_id,
        providerName: data.provider_name,
        organizationId: data.provider_id,
        organizationName: data.provider_name,
        periodStart: data.period_start,
        periodEnd: data.period_end,
        status: data.status,
        grossAmount,
        netAmount,
        taxDeduction,
        otherDeductions,
        totalProviderCut: netAmount,
        totalProviderVat: grossAmount - netAmount,
        totalAmount: grossAmount,
        approvalNotes: data.notes || null,
        disputeReason: data.provider_notes || null,
        supportingDocumentUrl: data.supporting_document_url || null,
        supportingDocumentName: data.supporting_document_name || null,
        payoutDetails,
        payoutCount: data.payout_count ?? payoutDetails.length,
        createdAt: data.created_at,
        canEdit: data.status === 'draft',
        canConfirmByProvider: data.status === 'draft' || data.status === 'pending_review',
        canConfirmByWtd: data.status === 'pending_review',
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        return null
      }
      throw e
    }
  },
}

/**
 * POST a payout statement action as multipart/form-data.
 *
 * The document is validated client-side first so an obviously bad file fails
 * with a precise code instead of a round trip.
 *
 * `Content-Type: null` REMOVES the header, and that is load-bearing: the shared
 * axios instance sets application/json globally, and axios silently serializes
 * a FormData body to JSON when a JSON content type is still present
 * (lib/defaults/index.js -> `hasJSONContentType ? JSON.stringify(...)`). The
 * file would be dropped and the backend would answer 400 document_required.
 * With the header gone, the browser sets multipart/form-data itself — only it
 * can generate the boundary. Note `undefined` is NOT equivalent: axios treats
 * it as "not provided" and the default survives.
 */
async function postPayoutStatementAction<T>(
  url: string,
  rawStatementId: string,
  document: File,
  textField: 'notes' | 'reason',
  textValue?: string | null
): Promise<T> {
  const documentError = validatePayoutDocument(document)
  if (documentError) throw payoutDocumentError(documentError)

  const statementId = decodeRelayId(rawStatementId) ?? rawStatementId

  const formData = new FormData()
  formData.append('statement_id', statementId)
  formData.append('document', document)

  const text = textValue?.trim()
  if (text) {
    formData.append(textField, text.slice(0, PAYOUT_TEXT_MAX_LENGTH))
  }

  try {
    const response = await apiClient.post<T>(url, formData, {
      // Must stay null — see the note above. Do not set a Content-Type here.
      headers: { 'Content-Type': null },
    })
    return response.data
  } catch (e) {
    if (axios.isAxiosError(e)) throw handleApiError(e)
    throw e
  }
}

/** Build a client-side rejection that mirrors the backend's error codes. */
function payoutDocumentError(code: PayoutDocumentErrorCode): ApiError {
  // status 0 marks this as never having reached the server.
  return new ApiError(code, code, 0)
}
